import React, { useEffect, useRef } from "react";
import { motion } from "motion/react";
import { useIntegrationsStore } from "@/hooks/useIntegrations";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import JiraSm from "@/assets/icons/JiraSm.svg";
import SlackSm from "@/assets/icons/SlackSm.svg";
import LinearSm from "@/assets/icons/LinearSm.svg";
import CheckWhite from "@/assets/icons/CheckWhite.svg";
import { toast } from "sonner";
import useUserData from "@/hooks/useUserData";
import { API_URL } from "@/env";
import { showCustomToast } from "../CustomToast";

interface IntegrationsListProps {
  setShowIntegrationsDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowJiraModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowLinearModal: React.Dispatch<React.SetStateAction<boolean>>;
  reportId?: string; // Optional: pass the current report ID
}

const IntegrationsList = ({
  setShowIntegrationsDropdown,
  setShowJiraModal,
  setShowLinearModal,
  reportId,
}: IntegrationsListProps) => {
  const {
    integrations,
    setSelectedIntegration,
    selectedIntegration,
    checkJiraStatus,
    checkLinearStatus,
  } = useIntegrationsStore();
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore();
  const { user } = useUserData();

  const handleJiraClick = () => {
    if (!integrations.jira.connected) {
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const url = `${API_URL}/jira/auth/${currentWorkspace?._id}`;

      const popup = window.open(
        url,
        "Jira Authorization",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
      );

      if (!popup) {
        showCustomToast("Please allow popups for this site");
        return;
      }

      // Poll for popup closure and check status
      const checkInterval = setInterval(async () => {
        if (popup.closed && currentWorkspace) {
          clearInterval(checkInterval);

          // Check if Jira was connected
          try {
            const status: any = await checkJiraStatus(currentWorkspace?._id);

            if (status?.connected) {
              setSelectedIntegration("jira");
              setShowIntegrationsDropdown(false);
              showCustomToast("Jira connected successfully", "check");
            }
          } catch (err) {
            console.error("Failed to check Jira status:", err);
          }
        }
      }, 500);
    } else {
      if (!reportId) {
        toast.error("No report selected");
        return;
      }

      setSelectedIntegration("jira");
      setShowJiraModal(true);
    }
  };

  const handleLinearClick = () => {
    if (!integrations.linear.connected) {
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const url = `${API_URL}/linear/auth/${currentWorkspace?._id}`;

      const popup = window.open(
        url,
        "Linear Authorization",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
      );

      if (!popup) {
        showCustomToast("Please allow popups for this site");
        return;
      }

      // Poll for popup closure and check status
      const checkInterval = setInterval(async () => {
        if (popup.closed && currentWorkspace) {
          clearInterval(checkInterval);

          // Check if Linear was connected
          try {
            const status: any = await checkLinearStatus(currentWorkspace?._id);

            if (status?.connected) {
              setSelectedIntegration("linear");
              setShowIntegrationsDropdown(false);
              showCustomToast("Linear connected successfully", "check");
            }
          } catch (err) {
            console.error("Failed to check Linear status:", err);
          }
        }
      }, 500);
    } else {
      if (!reportId) {
        toast.error("No report selected");
        return;
      }

      setSelectedIntegration("linear");
      setShowLinearModal(true);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute dropdown-shadow top-[calc(100%+4px)] text-[14px] leading-[20px] right-0 p-[4px] bg-gray-400 w-[240px] rounded-[16px]"
      >
        <div
          onClick={handleJiraClick}
          className="flex font-medium justify-between hover:bg-gray-500 transition-all rounded-[12px] cursor-pointer items-center h-[36px] pl-[10px] pr-[12px]"
        >
          <div className="flex gap-1.5 items-center">
            <img src={JiraSm} alt="Jira" />
            {integrations.jira.connected ? "Jira Issue" : "Connect Jira"}
          </div>
          {selectedIntegration === "jira" && (
            <img src={CheckWhite} alt="Selected" />
          )}
        </div>
        <div
          onClick={handleLinearClick}
          className="flex font-medium justify-between hover:bg-gray-500 transition-all rounded-[12px] cursor-pointer items-center h-[36px] pl-[10px] pr-[12px]"
        >
          <div className="flex gap-1.5 items-center">
            <img src={LinearSm} alt="Linear" />
            {integrations.linear.connected ? "Linear Issue" : "Connect Linear"}
          </div>
          {selectedIntegration === "linear" && (
            <img src={CheckWhite} alt="Selected" />
          )}
        </div>
        {/* <div
          onClick={() => {
            setSelectedIntegration("slack");
            setShowIntegrationsDropdown(false);
          }}
          className="flex font-medium justify-between hover:bg-gray-500 transition-all rounded-[12px] cursor-pointer items-center h-[36px] pl-[10px] pr-[12px]"
        >
          <div className="flex gap-1.5 items-center">
            <img src={SlackSm} alt="Slack" />
            {integrations.slack.connected ? "Slack message" : "Connect Slack"}
          </div>
          {selectedIntegration === "slack" && (
            <img src={CheckWhite} alt="Selected" />
          )}
        </div> */}
      </motion.div>
    </>
  );
};

export default IntegrationsList;
